import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable, inject, OnDestroy } from '@angular/core'
import { environment } from 'src/environments/environment'
import { isUnread } from './shared/utils/message.utils'
import { H2HConversation } from '../types/message'
import {
  BehaviorSubject,
  Observable,
  Subscription,
  timer,
  map,
  switchMap,
  catchError,
  of,
  tap,
} from 'rxjs'
import { AuthService } from './auth.service'
import { Router, NavigationEnd } from '@angular/router'
import { filter } from 'rxjs/operators'

@Injectable({
  providedIn: 'root',
})
export class MessageService implements OnDestroy {
  private readonly http = inject(HttpClient)
  private readonly authService = inject(AuthService)
  private readonly router = inject(Router)
  private readonly POLLING_INTERVAL = 10000
  private pollingSubscription?: Subscription
  private isMessagesTab = false

  // List of spam keywords that will trigger automatic reporting
  private readonly SPAM_KEYWORDS = [
    'kindly',
    'dear',
    'blessings',
    'urgently',
    'whatsapp',
    'telegram',
    'contact me at',
    'god bless',
    'inquiry',
    'transaction',
    'overseas',
    'payment',
    'wire transfer',
  ]

  conversations: Record<string, H2HConversation> = {}
  hasUnreadMessages$ = new BehaviorSubject<boolean>(false)

  constructor() {
    // Monitor router events to track when user is on the messages tab
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) => {
        const isOnMessagesTab = event.url.includes('/account/messages')

        // If navigating to messages tab, start polling
        if (isOnMessagesTab && !this.isMessagesTab) {
          this.isMessagesTab = true
          this.startPollingUnreadMessages()
          console.log('Started message polling - user is on messages tab')
        }
        // If navigating away from messages tab, stop polling
        else if (!isOnMessagesTab && this.isMessagesTab) {
          this.isMessagesTab = false
          this.stopPollingUnreadMessages()
          console.log('Stopped message polling - user left messages tab')
        }
      })

    // Initial check - are we already on the messages tab?
    const currentUrl = this.router.url
    if (currentUrl.includes('/account/messages')) {
      this.isMessagesTab = true
      this.startPollingUnreadMessages()
      console.log('Started message polling - initialized on messages tab')
    }
  }

  searchConversations(messageSearchParams: MessageSearchParams) {
    return this.http.post<H2HConversation[]>(
      `${environment.apiBaseUrl}/message/search`,
      messageSearchParams,
    )
  }

  getMyConversations() {
    return this.http.get<H2HConversation[]>(
      `${environment.apiBaseUrl}/v2/message/my`,
    )
  }

  getMyConversationsWithPagination(
    messageLimit?: number,
    conversationLimit?: number,
  ) {
    let params = new HttpParams()

    if (messageLimit !== undefined) {
      params = params.set('messageLimit', messageLimit.toString())
    }

    if (conversationLimit !== undefined) {
      params = params.set('conversationLimit', conversationLimit.toString())
    }

    console.log(
      '[MessageService] getMyConversationsWithPagination called with:',
      {
        messageLimit,
        conversationLimit,
        params: params.toString(),
      },
    )

    return this.http
      .get<
        | {
            conversations: H2HConversation[]
            pagination: {
              messagesPerConversation: number
              conversationLimit: number
              totalConversations: number
            }
          }
        | H2HConversation[]
      >(`${environment.apiBaseUrl}/v2/message/my`, { params })
      .pipe(
        tap((response) => {
          console.log('[MessageService] API response received:', response)
          console.log('[MessageService] Response type check:', {
            isArray: Array.isArray(response),
            hasConversations: 'conversations' in response,
            hasPagination: 'pagination' in response,
            responseKeys: Object.keys(response),
          })
        }),
        map((response) => {
          // Normalize the response to always return the expected format
          if (Array.isArray(response)) {
            // Old format - convert to new format
            return {
              conversations: response,
              pagination: {
                messagesPerConversation: messageLimit || 100,
                conversationLimit: conversationLimit || 100,
                totalConversations: response.length,
              },
            }
          } else {
            // New format - return as is
            return response as {
              conversations: H2HConversation[]
              pagination: {
                messagesPerConversation: number
                conversationLimit: number
                totalConversations: number
              }
            }
          }
        }),
      )
  }

  markMessagesAsRead(id: string) {
    this.stopPollingUnreadMessages()
    return this.http
      .put<
        H2HConversation[]
      >(`${environment.apiBaseUrl}/message/${id}/read`, {})
      .pipe(
        switchMap(() => this.getMyConversations()),
        map((conversations) => {
          const userUuid = this.authService.getUuid()
          const hasUnread = conversations.some((convo) =>
            convo.messages?.some(
              (message) =>
                isUnread(message.read) && message.senderUuid !== userUuid,
            ),
          )
          this.hasUnreadMessages$.next(hasUnread)

          // Only restart polling if still on messages tab
          if (this.isMessagesTab) {
            this.startPollingUnreadMessages()
          }

          return conversations
        }),
      )
  }

  getConversation(id: string): Observable<H2HConversation> {
    return this.http.get<H2HConversation>(
      `${environment.apiBaseUrl}/message/${id}`,
    )
  }

  loadMoreMessagesForConversation(
    conversationId: string,
  ): Observable<H2HConversation> {
    // Use the existing getConversation endpoint which returns all messages
    // This will give us the full conversation with all messages
    return this.getConversation(conversationId)
  }

  sendMessage(message: SendMessageBody) {
    // Use the v2 API endpoint for messaging
    return this.http.post(`${environment.apiBaseUrl}/v2/message`, message)
  }

  reportConversationAsSpam(
    conversationId: string,
    petId: string,
    petOwnerUserId: string,
    petOwnerEmail: string,
    reporterEmail: string = '',
    details: string = '',
    reportedMessage: string = '',
  ): Observable<any> {
    this.trackReportedSpamConversation(conversationId)

    // Get current user ID if available
    const currentUserUuid = this.authService.getUuid() || ''

    // Debug: Log the values being sent to the API
    console.log('Sending spam report to API:', {
      conversationId,
      petId,
      petOwnerUserId,
      petOwnerEmail,
      reporterUserId: currentUserUuid,
      reporterEmail,
      reason: 'Reported Message',
      details,
      reportedMessage,
    })

    // Ensure petId is not empty
    if (!petId) {
      console.error('petId is empty in reportConversationAsSpam')
      return new Observable((subscriber) => {
        subscriber.error({ error: 'Missing required petId' })
      })
    }

    // Use the new API endpoint
    return this.http
      .post(`${environment.apiBaseUrl}/utils/spam`, {
        conversationId: conversationId,
        petId: petId,
        petOwnerUserId: petOwnerUserId,
        petOwnerEmail: petOwnerEmail,
        reporterUserId: currentUserUuid,
        reporterEmail: reporterEmail,
        reason: 'Reported Message',
        details: details || '',
        reportedMessage: reportedMessage || '',
      })
      .pipe(
        catchError((error) => {
          console.error('Error calling spam API', error)
          throw error
        }),
      )
  }

  /**
   * Locally stores a reported conversation to prevent duplicate reports
   * and provide user feedback without backend integration
   */
  trackReportedSpamConversation(conversationId: string): void {
    try {
      // Get existing reported conversations from local storage
      const reportedConversationsStr =
        localStorage.getItem('reportedSpamConversations') || '[]'
      const reportedConversations = JSON.parse(
        reportedConversationsStr,
      ) as string[]

      // Add the conversation if not already reported
      if (!reportedConversations.includes(conversationId)) {
        reportedConversations.push(conversationId)
        localStorage.setItem(
          'reportedSpamConversations',
          JSON.stringify(reportedConversations),
        )
      }
    } catch (error) {
      console.error('Error tracking reported conversation', error)
    }
  }

  /**
   * Checks if a conversation has been previously reported as spam
   */
  isConversationReportedAsSpam(conversationId: string): boolean {
    try {
      const reportedConversationsStr =
        localStorage.getItem('reportedSpamConversations') || '[]'
      const reportedConversations = JSON.parse(
        reportedConversationsStr,
      ) as string[]
      return reportedConversations.includes(conversationId)
    } catch (error) {
      console.error('Error checking if conversation is reported', error)
      return false
    }
  }

  detectScamWords(message: string) {
    const regexpScam = new RegExp(
      /(payment|credit|card|pay|cash|money|email)/,
      'gi',
    )
    const regexpLink = new RegExp(
      /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/,
      'gi',
    )
    const regexpEmail = new RegExp(
      /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}/,
      'gi',
    )
    return {
      email: regexpEmail.test(message),
      link: regexpLink.test(message),
      scam: regexpScam.test(message),
    }
  }

  /**
   * Checks if a message contains any spam keywords
   * @param message The message text to check
   * @returns Object with the matched keyword if found, or null if no match
   */
  detectSpamKeywords(message: string): {
    matched: boolean
    keyword: string | null
  } {
    if (!message) return { matched: false, keyword: null }

    // Explicitly convert message to lowercase for case-insensitive comparison
    const normalizedMessage = message.toLowerCase().trim()

    for (const keyword of this.SPAM_KEYWORDS) {
      // Convert keyword to lowercase for comparison
      const normalizedKeyword = keyword.toLowerCase()

      // Check for word match, handling multi-word keywords
      if (normalizedKeyword.includes(' ')) {
        // For phrases, check if the message contains the exact phrase
        if (normalizedMessage.includes(normalizedKeyword)) {
          return { matched: true, keyword: keyword }
        }
      } else {
        // For single words, use word boundaries
        const regex = new RegExp(`\\b${normalizedKeyword}\\b`, 'i')
        if (regex.test(normalizedMessage)) {
          return { matched: true, keyword: keyword }
        }
      }
    }

    return { matched: false, keyword: null }
  }

  /**
   * Automatically report a message as spam based on keyword detection
   * @param conversationId The conversation ID
   * @param petId The pet ID
   * @param petOwnerUserId The pet owner's user ID
   * @param petOwnerEmail The pet owner's email
   * @param reporterEmail The reporter's email
   * @param message The message that contains spam
   * @param matchedKeyword The keyword that triggered the spam detection
   */
  autoReportSpam(
    conversationId: string,
    petId: string,
    petOwnerUserId: string,
    petOwnerEmail: string,
    reporterEmail: string = '',
    message: string = '',
    matchedKeyword: string = '',
  ): Observable<any> {
    this.trackReportedSpamConversation(conversationId)

    // Get current user ID if available
    const currentUserUuid = this.authService.getUuid() || ''

    // Debug: Log the values being sent to the API
    console.log('Sending auto spam report to API:', {
      conversationId,
      petId,
      petOwnerUserId,
      petOwnerEmail,
      reporterUserId: currentUserUuid,
      reporterEmail,
      reason: 'Auto Reported',
      details: `Matched keyword: ${matchedKeyword}`,
      reportedMessage: message || '',
    })

    // Ensure petId is not empty
    if (!petId) {
      console.error('petId is empty in autoReportSpam')
      return new Observable((subscriber) => {
        subscriber.error({ error: 'Missing required petId' })
      })
    }

    return this.http
      .post(`${environment.apiBaseUrl}/utils/spam`, {
        conversationId: conversationId,
        petId: petId,
        petOwnerUserId: petOwnerUserId,
        petOwnerEmail: petOwnerEmail,
        reporterUserId: currentUserUuid,
        reporterEmail: reporterEmail,
        reason: 'Auto Reported',
        details: `Matched keyword: ${matchedKeyword}`,
        reportedMessage: message || '',
      })
      .pipe(
        catchError((error) => {
          console.error('Error auto-reporting spam', error)
          throw error
        }),
      )
  }

  private pollForUnreadMessages() {
    const userUuid = this.authService.getUuid()
    if (!userUuid) return

    // Only poll when on messages tab
    if (!this.isMessagesTab) {
      console.log('Not polling for messages - not on messages tab')
      return
    }

    this.pollingSubscription = timer(0, this.POLLING_INTERVAL)
      .pipe(
        switchMap(() => this.getMyConversations()),
        map((conversations) =>
          conversations.some((convo) =>
            convo.messages?.some(
              (message) =>
                isUnread(message.read) && message.senderUuid !== userUuid,
            ),
          ),
        ),
      )
      .subscribe((hasUnread) => {
        this.hasUnreadMessages$.next(hasUnread)
      })
  }

  /**
   * Start polling for unread messages
   * Only runs when on the messages tab
   */
  public startPollingUnreadMessages() {
    // Don't start polling if not on the messages tab
    if (!this.isMessagesTab) {
      console.log('Not starting polling - not on messages tab')
      return
    }

    if (!this.pollingSubscription || this.pollingSubscription.closed) {
      console.log('Starting message polling')
      this.pollForUnreadMessages()
    }
  }

  public stopPollingUnreadMessages() {
    if (this.pollingSubscription) {
      console.log('Stopping message polling')
      this.pollingSubscription.unsubscribe()
    }
  }

  ngOnDestroy() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe()
    }
  }

  /**
   * Navigate to the messages tab with a specific conversation selected
   * @param conversationId The ID of the conversation to select
   */
  navigateToConversation(conversationId: string) {
    this.router.navigate(['/account/messages'], {
      queryParams: { conversationId },
    })
  }

  /**
   * Get conversations that have been reported as spam (admin only)
   */
  getSpamReportConversationsForAdmin(): Observable<H2HConversation[]> {
    return this.http
      .get<H2HConversation[]>(`${environment.apiBaseUrl}/admin/message/spam`, {
        headers: {
          Authorization: `Bearer ${this.authService.accessToken}`,
        },
      })
      .pipe(
        catchError((error) => {
          console.error('Error fetching spam report conversations:', error)
          return of([])
        }),
      )
  }

  /**
   * Mark messages as read as admin (impersonation)
   */
  markMessagesAsReadAsAdmin(id: string): Observable<any> {
    return this.http
      .put<any>(
        `${environment.apiBaseUrl}/admin/message/${id}/read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${this.authService.accessToken}`,
          },
        },
      )
      .pipe(
        catchError((error) => {
          console.error('Error marking messages as read as admin:', error)
          return of(null)
        }),
      )
  }
}

export type MessageSearchParams = {
  creatorUuid?: string
  petUuid?: string
  'participants.user1'?: string
  'participants.user2'?: string
}

export type SendMessageBody = {
  message: string
  petUuid: string
  conversationUuid?: string
  isOwnerVerified?: boolean
}
